import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../api/client';
import './Book.css';
// reuse reviews hero styles for consistent header look
import '../Reviews/Reviews.css';

/* ── Helpers ── */

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function nightsBetween(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

const PRICE_PER_NIGHT = 350; // RON

/* ══════════════════════════════════════ */
export default function BookPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Locale-aware month / day labels from translations
  const MONTH_NAMES = t('book.months');
  const DAY_LABELS = t('book.days');
  const locale = language === 'ro' ? 'ro-RO' : 'en-US';

  /* Calendar state */
  const today = useMemo(() => {
    const d = new Date(); d.setHours(0,0,0,0); return d;
  }, []);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  /* Selection state */
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  /* Booking form */
  const [guests, setGuests] = useState(2);
  const [notes, setNotes] = useState('');

  /* Booked dates from server */
  const [bookedRanges, setBookedRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  /* Fetch existing bookings */
  const fetchBookings = useCallback(async () => {
    try {
      const data = await api.getBookings();
      setBookedRanges(data.bookings || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  /* ── Check if a date is booked ── */
  const isBooked = useCallback((date) => {
    const ds = toDateStr(date);
    return bookedRanges.some(b => ds >= b.check_in.slice(0,10) && ds < b.check_out.slice(0,10));
  }, [bookedRanges]);

  /* ── Check if range has booked dates inside ── */
  const rangeHasConflict = useCallback((start, end) => {
    let cur = new Date(start);
    while (cur < end) {
      if (isBooked(cur)) return true;
      cur = addDays(cur, 1);
    }
    return false;
  }, [isBooked]);

  /* ── Build calendar grid (Mon-start) ── */
  const calendarDays = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const last = new Date(viewYear, viewMonth + 1, 0);
    let startDay = first.getDay(); // 0=Sun
    startDay = startDay === 0 ? 6 : startDay - 1; // convert to Mon=0

    const days = [];
    // leading blanks
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      days.push(new Date(viewYear, viewMonth, d));
    }
    return days;
  }, [viewMonth, viewYear]);

  /* ── Navigation ── */
  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const canGoPrev = viewYear > today.getFullYear() || (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  /* ── Day click ── */
  const handleDayClick = (date) => {
    if (!date || date < today || isBooked(date)) return;

    if (!checkIn || (checkIn && checkOut)) {
      // Start fresh selection
      setCheckIn(date);
      setCheckOut(null);
      setError(null);
      setSuccess(null);
    } else {
      // Second click — set checkout
      if (date <= checkIn) {
        setCheckIn(date);
        setCheckOut(null);
      } else {
        if (rangeHasConflict(checkIn, date)) {
          setError(t('book.errorRange'));
          return;
        }
        setCheckOut(date);
      }
    }
  };

  /* ── Day classes ── */
  const getDayClass = (date) => {
    if (!date) return 'book__day--empty';
    const classes = ['book__day'];

    const isPast = date < today;
    const booked = isBooked(date);

    if (isPast) classes.push('book__day--past');
    if (booked) classes.push('book__day--booked');
    if (isSameDay(date, today)) classes.push('book__day--today');

    if (checkIn && isSameDay(date, checkIn)) classes.push('book__day--start');
    if (checkOut && isSameDay(date, checkOut)) classes.push('book__day--end');

    // In-range
    if (checkIn && checkOut && date > checkIn && date < checkOut) {
      classes.push('book__day--range');
    }

    // Hover preview
    if (checkIn && !checkOut && hoverDate && date > checkIn && date <= hoverDate && !isPast && !booked) {
      classes.push('book__day--hover-range');
    }

    return classes.join(' ');
  };

  /* ── Computed ── */
  const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
  const total = nights * PRICE_PER_NIGHT;

  /* ── Submit booking ── */
  const handleSubmit = async () => {
    if (!user) return;
    if (!checkIn || !checkOut) { setError(t('book.errorDates')); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.createBooking(toDateStr(checkIn), toDateStr(checkOut), guests, notes || null);
      setSuccess(t('book.successMsg'));
      setCheckIn(null);
      setCheckOut(null);
      setGuests(2);
      setNotes('');
      fetchBookings();
    } catch (err) {
      setError(err.message || t('book.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  /* ═══ RENDER ═══ */
  return (
    <div className="book">
      {/* Hero banner */}
      {/* Hero banner (styled like Reviews) */}
      <section className="rv rv__hero book__hero">
        <div className="rv__hero-bg">
          <div className="rv__hero-gradient" />
          <div className="rv__hero-pattern" />
          <div className="rv__hero-vignette" />
        </div>

        <div className="rv__hero-particles">
          {[...Array(20)].map((_, i) => (
            <span key={i} className="rv__hero-particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              animationDuration: `${5 + Math.random() * 7}s`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              opacity: 0.12 + Math.random() * 0.22,
            }} />
          ))}
        </div>

        <div className="rv__hero-leaf rv__hero-leaf--1" />
        <div className="rv__hero-leaf rv__hero-leaf--2" />
        <div className="rv__hero-leaf rv__hero-leaf--3" />

        <div className="rv__hero-mountains">
          <svg className="rv__hero-mountain rv__hero-mountain--back" viewBox="0 0 1440 200" preserveAspectRatio="none">
            <path d="M0,200 L0,120 Q180,40 360,100 Q540,30 720,80 Q900,20 1080,70 Q1260,40 1440,90 L1440,200 Z" />
          </svg>
          <svg className="rv__hero-mountain rv__hero-mountain--front" viewBox="0 0 1440 200" preserveAspectRatio="none">
            <path d="M0,200 L0,150 Q200,100 400,140 Q600,80 800,130 Q1000,90 1200,120 Q1350,105 1440,140 L1440,200 Z" />
          </svg>
        </div>

        <div className="rv__hero-content" data-reveal>
          <div className="rv__hero-tagline">
            <span className="rv__hero-tagline-line" />
            <span className="rv__hero-tagline-text">{t('book.heroLabel')}</span>
            <span className="rv__hero-tagline-line" />
          </div>
          <h1 className="rv__hero-title">{t('book.heroTitleMain')} <em>{t('book.heroTitleAccent')}</em></h1>
          <p className="rv__hero-sub">{t('book.heroSub')}</p>

          <div className="rv__hero-quill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/>
              <line x1="16" y1="8" x2="2" y2="22"/>
            </svg>
            <div className="rv__hero-quill-trail" />
          </div>
        </div>

        <div className="rv__hero-book-spine">
          <div className="rv__hero-spine-line" />
          <div className="rv__hero-spine-diamond" />
          <div className="rv__hero-spine-line" />
        </div>
      </section>

      {/* Main content */}
      <section className="book__main">
        <div className="book__container">

          {/* ── Left: Calendar ── */}
          <div className="book__calendar-section" data-reveal>
            <div className="book__calendar">
              {/* Calendar header */}
              <div className="book__cal-header">
                <button
                  className="book__cal-nav"
                  onClick={prevMonth}
                  disabled={!canGoPrev}
                  aria-label="Previous month"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div className="book__cal-title">
                  <span className="book__cal-month">{MONTH_NAMES[viewMonth]}</span>
                  <span className="book__cal-year">{viewYear}</span>
                </div>
                <button className="book__cal-nav" onClick={nextMonth} aria-label="Next month">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 6 15 12 9 18"/></svg>
                </button>
              </div>

              {/* Day labels */}
              <div className="book__cal-days-header">
                {DAY_LABELS.map(d => (
                  <span key={d} className="book__cal-day-label">{d}</span>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="book__cal-grid">
                {loading ? (
                  <div className="book__cal-loading">
                    <div className="book__spinner" />
                  </div>
                ) : (
                  calendarDays.map((date, i) => (
                    <button
                      key={i}
                      className={getDayClass(date)}
                      onClick={() => handleDayClick(date)}
                      onMouseEnter={() => date && setHoverDate(date)}
                      onMouseLeave={() => setHoverDate(null)}
                      disabled={!date || date < today || isBooked(date)}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  ))
                )}
              </div>

              {/* Legend */}
              <div className="book__cal-legend">
                <div className="book__cal-legend-item">
                  <span className="book__legend-dot book__legend-dot--available" />
                  <span>{t('book.available')}</span>
                </div>
                <div className="book__cal-legend-item">
                  <span className="book__legend-dot book__legend-dot--booked" />
                  <span>{t('book.booked')}</span>
                </div>
                <div className="book__cal-legend-item">
                  <span className="book__legend-dot book__legend-dot--selected" />
                  <span>{t('book.selected')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Booking panel ── */}
          <div className="book__panel" data-reveal>

            {/* Not logged in */}
            {!user && (
              <div className="book__auth-gate">
                <div className="book__auth-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <h3 className="book__auth-title">{t('book.signInReq')}</h3>
                <p className="book__auth-desc">
                  {t('book.signInDesc')}
                </p>
                <Link to="/login" className="book__auth-btn">
                  <span>{t('book.signInBtn')}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
                <p className="book__auth-register">
                  {t('book.noAccount')} <Link to="/register">{t('book.createOne')}</Link>
                </p>
              </div>
            )}

            {/* Logged in — booking form */}
            {user && (
              <div className="book__form">
                <h3 className="book__form-title">{t('book.yourReservation')}</h3>

                {/* Date display */}
                <div className="book__dates">
                  <div className={`book__date-box${checkIn ? ' book__date-box--active' : ''}`}>
                    <span className="book__date-label">{t('book.checkIn')}</span>
                    <span className="book__date-value">
                      {checkIn ? checkIn.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : t('book.selectDate')}
                    </span>
                  </div>
                  <div className="book__dates-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                  <div className={`book__date-box${checkOut ? ' book__date-box--active' : ''}`}>
                    <span className="book__date-label">{t('book.checkOut')}</span>
                    <span className="book__date-value">
                      {checkOut ? checkOut.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : t('book.selectDate')}
                    </span>
                  </div>
                </div>

                {/* Guests */}
                <div className="book__field">
                  <label className="book__field-label">{t('book.guests')}</label>
                  <div className="book__guests-control">
                    <button
                      className="book__guests-btn"
                      onClick={() => setGuests(g => Math.max(1, g - 1))}
                      disabled={guests <= 1}
                    >-</button>
                    <span className="book__guests-value">{guests}</span>
                    <button
                      className="book__guests-btn"
                      onClick={() => setGuests(g => Math.min(10, g + 1))}
                      disabled={guests >= 10}
                    >+</button>
                  </div>
                </div>

                {/* Notes */}
                <div className="book__field">
                  <label className="book__field-label">{t('book.specialNotes')}</label>
                  <textarea
                    className="book__textarea"
                    placeholder={t('book.notesPlaceholder')}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Price summary */}
                {nights > 0 && (
                  <div className="book__summary">
                    <div className="book__summary-row">
                      <span>{PRICE_PER_NIGHT} RON x {nights} {t('book.nights')}</span>
                      <span>{total.toLocaleString()} RON</span>
                    </div>
                    <div className="book__summary-divider" />
                    <div className="book__summary-row book__summary-row--total">
                      <span>{t('book.total')}</span>
                      <span>{total.toLocaleString()} RON</span>
                    </div>
                  </div>
                )}

                {/* Error / Success */}
                {error && <div className="book__msg book__msg--error">{error}</div>}
                {success && <div className="book__msg book__msg--success">{success}</div>}

                {/* Submit */}
                <button
                  className="book__submit"
                  onClick={handleSubmit}
                  disabled={!checkIn || !checkOut || submitting}
                >
                  {submitting ? (
                    <span className="book__spinner book__spinner--sm" />
                  ) : (
                    <>
                      <span>{t('book.confirmBtn')}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
